import { Pipe, PipeTransform } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
  ConfiguratorService,
  GetConfigParams,
  GetConfigResponse,
  GetFilteredPlansParams,
  GetFilteredPlansResponse,
  PlanClass,
  PlansService,
  TranslationService,
} from 'src/app/core';
import { brandNames } from 'src/app/core/store/brands/brands.selectors';
import { endUsers } from 'src/app/core/store/consumers/consumers.selectors';
import { distributorNames } from 'src/app/core/store/distributors/distributors.selectors';
import { plans } from 'src/app/core/store/plans/plans.selectors';
import { LocalizedDatePipe } from 'src/app/shared/pipes/localized-date.pipe';

@Pipe({ name: 'ValueComputer' })
export class ValueComputerPipe implements PipeTransform {
  constructor(
    private store: Store,
    private configuratorService: ConfiguratorService,
    private translationService: TranslationService,
    private plansService: PlansService
  ) {}
  private dateFormat: string = 'MMM d, yyyy';
  transform(value: any, selector?: string, data?: any): Observable<string> {
    switch (selector) {
      case 'VALUE_COMMON_BRAND':
        return this.store.pipe(
          select(brandNames),
          filter((brandName) => !!brandName),
          map((brandName) => {
            const brand =
              brandName.brandDisplayNames &&
              brandName.brandDisplayNames.find((b) => b.brandId === value);
            return (brand && brand.brandName) || 'N/A';
          })
        );

      case 'VALUE_SIM_INFO_STATUS':
        return of(
          data?.status
            ? value
            : 'PAGE.MOBILE.SIM_AND_LINE_MANAGEMENT.SIM.TABLE.COLUMNS.STATUS.UNDEFINED'
        );

      case 'VALUE_LINE_INFO_STATUS':
        return of(
          data?.status
            ? value
            : 'PAGE.MOBILE.SIM_AND_LINE_MANAGEMENT.LINE.TABLE.COLUMNS.STATUS.UNDEFINED'
        );

      case 'VALUE_COMMON_DISTRIBUTOR':
        return this.store.pipe(
          select(distributorNames),
          filter((distributorName) => !!distributorName),
          map((distributorName) => {
            const distributor =
              distributorName.distributors &&
              distributorName.distributors[value];
            return (distributor && distributor.displayName) || 'N/A';
          })
        );

      case 'VALUE_COMMON_LINE_TYPE':
        const getConfigParams: GetConfigParams = {
          namespace: 'BrandLineConfigurationType',
          owner: data,
        };
        return this.configuratorService.getConfig(getConfigParams).pipe(
          map((getConfigResponse: GetConfigResponse) => {
            return getConfigResponse.values[value];
          })
        );

      case 'VALUE_COMMON_PLAN_BASE':
        const getFilteredPlans: GetFilteredPlansParams = {
          brandId: data,
          class: PlanClass.BASE,
        };

        return this.plansService
          .getFilteredPlansInternal(getFilteredPlans)
          .pipe(
            map((res: GetFilteredPlansResponse) => {
              const plan =
                res.plans?.filter((plan) => plan.planUuid === value) || [];
              if (plan[0]?.displayName) {
                return plan[0].displayName;
              }
              return 'N/A';
            })
          );

      case 'VALUE_LINE_PHONENUMBER.PHONENUMBER':
        if (!value || !value.length || !value[0].phoneNumber) {
          return of('N/A');
        }

        if (value.length > 1 && value[0].phoneNumber) {
          let templateValue = '';
          for (let i = 0; i < value.length; i++) {
            templateValue = templateValue + value[i].phoneNumber + '\u2028';
          }
          return of(templateValue);
        }
        return of(value[0].phoneNumber);

      case 'VALUE_LINE_PHONENUMBER.PREFERED_AREA_CODE':
        // debugger
        if (!value || !value.length) {
          return of('N/A');
        }

        if (value.preferredAreaCode) {
          return of (value.preferredAreaCode);
        }

        if (Array.isArray(value) && value[0].hasOwnProperty('preferredAreaCode')) {
          return of (value[0].preferredAreaCode);
        }
        return of(value ? value.preferredAreaCode || 'N/A' : 'N/A');

      case 'VALUE_LINE_SIM.ICCID':
        return of(value ? value.iccid || 'N/A' : 'N/A');

      case 'VALUE_ARRAY':
        if (!value) {
          return of('N/A');
        }
        if (value.length > 1) {
          let templateValue = '';
          for (let i = 0; i < value.length; i++) {
            templateValue = templateValue + value[i] + '<br />';
          }
          return of(templateValue);
        }
        return of(value);

      case 'VALUE_LINE_PREPROVISIONPLAN.PLANNAME':
        if (!value || !value.preProvisionedPlan) {
          return of('N/A');
        }

        return of(value ? value.preProvisionedPlan.planDisplayName || 'N/A' : 'N/A');

      case 'VALUE_LINE_PREPROVISIONPLAN.BILLTO':
        if (!value || !value.preProvisionedPlan?.billToAccountName) {
          return of('N/A');
        }

        return of(value ? value?.preProvisionedPlan?.billToAccountName || 'N/A' : 'N/A');

      case 'VALUE_LINE_PREPROVISIONPLAN.BILLON':
        if (!value || !value.preProvisionedPlan?.billOn) {
          return of('N/A');
        }

        return of(value
          ?
          this.translationService.instant('GLOBAL_TERMS.BILL_ON_BILL_TO.BILL_ON_TYPE.'
            + value?.preProvisionedPlan?.billOn) || 'N/A'
          : 'N/A'
        );

      case 'VALUE_LINE_PREPROVISIONPLAN.PLANID':
        if (!value) {
          return of('N/A');
        }
        return this.store.pipe(
          select(plans),
          filter((plans) => !!plans),
          map((res) => {
            const plan =
              res.plans?.filter(
                (plan) => plan.planUuid === value?.plan?.planId
              ) || [];
            return (plan[0] && plan[0].displayName) || 'N/A';
          })
        );

      case 'VALUE_LINE_PREPROVISIONPLAN.MEMO':
        return of(value?.preProvisionedPlan?.memo ? value.preProvisionedPlan.memo : 'N/A');

      case 'VALUE_LINE_BRANDNAME':
        return of(value ? value : 'N/A');

      case 'VALUE_LINE_TYPE':
        if (!data || !data.lineType) {
          return of('N/A');
        }
        return of(`GLOBAL_TERMS.LINES.TYPE.${data.lineType}`);

      case 'VALUE_COMMON_ENDUSERID':
        if (!value) {
          return of('N/A');
        }
        return this.store.pipe(
          select(endUsers),
          map((endUsers) => {
            const endUser = endUsers.find(
              (endUser) => endUser.endUserId === value
            );
            return endUser && endUser.firstName && endUser.lastName
              ? `${endUser.firstName} ${endUser.lastName}`
              : 'N/A';
          })
        );

      case 'VALUE_COMMON_ENDUSERNAME':
        if (!value) {
          return of('N/A');
        }
        return of(value);

      case 'VALUE_COMMON_PURCHASEORDER':
        if (!value) {
          return of('N/A');
        }
        return of(value);

      case 'VALUE_COMMON_DATE':
        if (!value) {
          return of('N/A');
        }
        const format: string = this.dateFormat;
        const date = new Date(value);
        return of(
          new LocalizedDatePipe(this.translationService).transform(date, format)
        );

      default:
        break;
    }
    if (!value) {
      return of('N/A');
    }
    return of(value);
  }
}
